import { MAIN_COLLECTION_ID, type CollectionId } from '@platewell/core';
import { useStore, type CollectionSummary } from '@platewell/query';
import { useState } from 'react';
import { Dialog } from './dialog.js';

type DeleteMode = 'move' | 'delete';

export interface DeleteCollectionDialogProps {
  collection: CollectionSummary;
  /** Collections the recipes can be moved into — every collection except the one being deleted. */
  targets: CollectionSummary[];
  onClose: () => void;
  onDeleted?: () => void;
}

/**
 * Confirms deleting a collection and decides what happens to the recipes inside it: move
 * them somewhere else, or send them to trash. Deleting the collection alone would leave
 * them for the reconciler to sweep into Main, which is rarely what someone means.
 */
export function DeleteCollectionDialog({
  collection,
  targets,
  onClose,
  onDeleted,
}: DeleteCollectionDialogProps) {
  const store = useStore();
  const defaultTarget =
    targets.find((c) => c.id === MAIN_COLLECTION_ID)?.id ?? targets[0]?.id ?? MAIN_COLLECTION_ID;
  const [mode, setMode] = useState<DeleteMode>('move');
  const [targetId, setTargetId] = useState<CollectionId>(defaultTarget);
  const [busy, setBusy] = useState(false);
  const count = collection.recipeCount;
  const canConfirm = !busy && !(count > 0 && mode === 'move' && targets.length === 0);

  async function confirm() {
    const id = collection.id;
    const recipes = store.listRecipes({ collectionId: id });
    if (mode === 'move') {
      for (const recipe of recipes) {
        await store.addRecipeToCollection(recipe.id, targetId);
        await store.removeRecipeFromCollection(recipe.id, id);
      }
    } else {
      for (const recipe of recipes) {
        await store.deleteRecipe(recipe.id);
      }
    }
    await store.deleteCollection(id);
    onDeleted?.();
  }

  return (
    <Dialog onClose={busy ? () => undefined : onClose} aria-label={`Delete “${collection.name}”`}>
      <h2>Delete “{collection.name}”?</h2>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (!canConfirm) return;
          setBusy(true);
          void confirm().catch(() => setBusy(false));
        }}
      >
        {count === 0 ? (
          <p className="hint">This collection is empty. It will be removed.</p>
        ) : (
          <>
            <p className="hint">
              This collection has {count} {count === 1 ? 'recipe' : 'recipes'}. Choose what happens
              to them.
            </p>
            <fieldset className="delete-collection-options" disabled={busy}>
              <label className="radio-row">
                <input
                  type="radio"
                  name="delete-mode"
                  checked={mode === 'move'}
                  onChange={() => setMode('move')}
                />
                <span>
                  Move recipes to
                  <select
                    className="select"
                    style={{ marginLeft: 8, maxWidth: 220 }}
                    value={targetId}
                    disabled={mode !== 'move' || targets.length === 0}
                    onChange={(event) => setTargetId(event.target.value as CollectionId)}
                    onClick={(event) => event.stopPropagation()}
                  >
                    {targets.map((target) => (
                      <option key={target.id} value={target.id}>
                        {target.name}
                      </option>
                    ))}
                  </select>
                </span>
              </label>
              <label className="radio-row">
                <input
                  type="radio"
                  name="delete-mode"
                  checked={mode === 'delete'}
                  onChange={() => setMode('delete')}
                />
                <span>
                  Delete all recipes in this collection
                  <span className="hint" style={{ display: 'block', marginTop: 4 }}>
                    Recipes that also belong elsewhere are still moved to trash. You can restore
                    them later.
                  </span>
                </span>
              </label>
            </fieldset>
          </>
        )}
        <div className="row" style={{ marginTop: 18 }}>
          <span className="spacer" />
          <button type="button" className="btn ghost" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn danger" disabled={!canConfirm}>
            {busy ? 'Deleting…' : 'Delete collection'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
